import { useState } from "react";
import { Modal, Upload, Button, Typography, message } from "antd";
import {
  CloudUploadOutlined,
  CloudDownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import btnStyles from "../../styles/Custom/Button.module.css";
import styles from "./BulkUploadModal.module.css";
import { getErrorMessage } from "../../helpers/error";

const { Dragger } = Upload;

const isValidXlsxFile = (file) => {
  const fileName = file?.name?.toLowerCase() || "";
  if (!fileName.endsWith(".xlsx")) {
    return false;
  }

  const invalidMimeTypes = [
    "text/csv",
    "application/vnd.ms-excel",
    "text/plain",
    "application/pdf",
  ];

  if (file?.type && invalidMimeTypes.includes(file.type)) {
    return false;
  }

  return true;
};

const BulkUploadModal = ({
  open,
  setOpen,
  refreshPage,
  title,
  requiredColumns = [],
  sampleFileName,
  uploadApi,
  fileField,
  sampleHint,
  sampleDownloadHint,
  prepareUploadFile,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setFileError(null);
    setUploadError(null);
    setSubmitting(false);
  };

  const showInvalidFileError = (fileName) => {
    const errorMessage = `Only .xlsx files are supported. "${fileName}" is not a valid file format.`;
    setFileError(errorMessage);
    message.error(errorMessage);
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.download = sampleFileName;
    link.href = `${process.env.PUBLIC_URL || ""}/${sampleFileName}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      message.error("Please select an Excel file to upload");
      return;
    }

    if (!isValidXlsxFile(selectedFile)) {
      showInvalidFileError(selectedFile.name);
      return;
    }

    setSubmitting(true);
    setUploadError(null);
    try {
      const fileToUpload = prepareUploadFile
        ? await prepareUploadFile(selectedFile)
        : selectedFile;
      const res = await uploadApi({ [fileField]: fileToUpload });
      message.success(res?.data?.message || "Upload successful");
      refreshPage?.();
      handleClose();
    } catch (err) {
      const errorMessage = getErrorMessage(err) || "Upload failed";
      setUploadError(errorMessage);
      message.error(errorMessage);
      setSubmitting(false);
    }
  };

  const uploadProps = {
    accept: ".xlsx",
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      if (!isValidXlsxFile(file)) {
        showInvalidFileError(file.name);
        return Upload.LIST_IGNORE;
      }

      setFileError(null);
      setUploadError(null);
      setSelectedFile(file);
      return false;
    },
    onRemove: () => {
      setSelectedFile(null);
      setFileError(null);
      setUploadError(null);
    },
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={title}
      width={720}
      centered
      destroyOnClose
      footer={
        <div className={styles.modalFooter}>
          <Button className={btnStyles.mdBtn} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            className={btnStyles.mdBtn}
            loading={submitting}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className={styles.bulkUploadContent}>
        <div className={styles.formatAlert}>
          <FileTextOutlined className={styles.formatAlertIcon} />
          <div>
            <Typography.Text strong className={styles.formatAlertTitle}>
              Supported format: .xlsx
            </Typography.Text>
            <Typography.Paragraph className={styles.formatAlertText}>
              Only Excel files using the provided sample template can be
              uploaded.
              {sampleHint ? ` ${sampleHint}` : ""}
            </Typography.Paragraph>
          </div>
        </div>

        {uploadError && (
          <div className={styles.uploadErrorAlert}>
            <CloseCircleOutlined className={styles.uploadErrorIcon} />
            <div>
              <Typography.Text strong className={styles.uploadErrorTitle}>
                Upload failed
              </Typography.Text>
              <Typography.Paragraph className={styles.uploadErrorText}>
                {uploadError}
              </Typography.Paragraph>
            </div>
          </div>
        )}

        {requiredColumns.length > 0 && (
          <div className={styles.requiredColumnsSection}>
            <Typography.Text className={styles.sectionLabel}>
              Required Columns
            </Typography.Text>
            <div className={styles.columnList}>
              {requiredColumns.map((column) => (
                <div key={column} className={styles.columnItem}>
                  {column}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.sampleDownloadCard}>
          <div className={styles.sampleDownloadLeft}>
            <FileExcelOutlined className={styles.sampleDownloadIcon} />
            <Typography.Text className={styles.sampleDownloadText}>
              {sampleDownloadHint ||
                "Download the sample file and use the same format for upload."}
            </Typography.Text>
          </div>
          <Button
            className={`${btnStyles.mdBtn} ${styles.sampleDownloadBtn}`}
            icon={<CloudDownloadOutlined />}
            onClick={handleDownloadSample}
          >
            Download Sample
          </Button>
        </div>

        <Dragger {...uploadProps} className={styles.uploadDragger}>
          <p className={styles.uploadDraggerIcon}>
            <CloudUploadOutlined />
          </p>
          <p className={styles.uploadDraggerTitle}>
            Drag &amp; drop your Excel file here
          </p>
          <p className={styles.uploadDraggerBrowse}>or click to browse</p>
          <p className={styles.uploadDraggerHint}>Only .xlsx files supported</p>
          {selectedFile && (
            <Typography.Text className={styles.selectedFileName}>
              Selected: {selectedFile.name}
            </Typography.Text>
          )}
          {fileError && (
            <Typography.Text type="danger" className={styles.selectedFileName}>
              {fileError}
            </Typography.Text>
          )}
        </Dragger>
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
